/** The three per-row count columns shared by the listener and source tables. */
export type CountColumn = 'Page Views' | 'Downloads/Saves' | 'Streams'

const eventTypeToColumnMapper: Record<string, CountColumn> = {
  access_track: 'Page Views',
  play_track: 'Streams',
  download_track: 'Downloads/Saves',
}
type SourceObject = {
  source_id: number
  source_type: string
  source_name: string
}
type Track = {
  name: string
  id: number
}
export type AggregatedStats = {
  pageViews: number
  downloads: number
  streams: number
  uniqueListeners: number
}
export type ListenerEvent = {
  source: SourceObject
  event_type: string
  // API returns ISO date strings, not Date objects
  timestamp: string
}
const getLastActivity = (events: ListenerEvent[]): Date | null => {
  const validDates = events
    .filter(event => Boolean(event.timestamp))
    .map(event => new Date(event.timestamp))
    .filter(date => !isNaN(date.getTime()))
  if (validDates.length === 0) return null
  return validDates.reduce((latest, date) => (date > latest ? date : latest))
}
type ListenerData = {
  events: ListenerEvent[]
  listener_name: string
  listener_id: number
}
/** The `Source` cell of a source row: display text plus an optional route. */
export type SourceCell = {
  sourceLink: string | null
  sourceText: string
}

/** One source's tally of a set of events. */
export type SourceCounts = {
  Source: SourceCell
  'Page Views': number
  'Downloads/Saves': number
  Streams: number
}

/** A row of the per-listener breakdown shown when a listener is expanded. */
export type ListenerSourceRow = SourceCounts

export type SourceRow = SourceCounts & {
  /** Listener ids, deduped — the table renders the count. */
  uniqueListeners: number[]
}

export type TransformedListenerData = {
  Name: string
  id: number
  'Page Views': number
  'Downloads/Saves': number
  Streams: number
  'Last Activity': Date | null
  /** Kept so an expanded row can be broken down by source without refetching. */
  events: ListenerEvent[]
}

const _getPlaylistName = sourceName => {
  // Playlist names get returned from the API wrapped in `<>` so we slice to get the name
  if (sourceName && sourceName.length > 2) {
    return `${sourceName.slice(1, sourceName.length - 1)}`
  } else {
    return `Playlist`
  }
}

const getSourceName = (source, track: Track) => {
  if (source.source_type === 'playlist') {
    let sourceName = source.source_name
    return _getPlaylistName(sourceName)
  }

  if (source.source_type === 'track') {
    return `Track: ${track.name}`
  }

  if (source.source_type === 'embed') {
    return `Embed Player: ${track.name}`
  }

  if (source.source_type === 'catalog') {
    return `Catalog: ${source.source_name}`
  }

  return 'Other Sources'
}

const getSourceLink = (source, track) => {
  if (!source) {
    return null
  }

  if (source.source_type === 'playlist') {
    return `/explorer/playlists/${source.source_id}`
  }

  if (source.source_type === 'track') {
    return `/explorer/tracks/${source.source_id}`
  }

  if (source.source_type === 'embed') {
    return `/explorer/tracks/${track.id}`
  }

  if (source.source_type === 'catalog') {
    return `/catalogs/${source.source_id}`
  }

  return null
}

const resolveSourceName = (event: ListenerEvent, track: Track): string =>
  event.source ? getSourceName(event.source, track) : 'Other Sources'

const emptySourceCounts = (
  event: ListenerEvent,
  track: Track,
  sourceText: string,
): SourceCounts => ({
  Source: {
    sourceLink: getSourceLink(event.source, track),
    sourceText,
  },
  'Page Views': 0,
  'Downloads/Saves': 0,
  Streams: 0,
})

/**
 * The count column an event belongs to, or null for event types we don't
 * track — without the null case those would land on an `undefined` key.
 */
const columnForEvent = (event: ListenerEvent): CountColumn | null =>
  eventTypeToColumnMapper[event.event_type] ?? null

export const makeSourceData: (
  listenerData: ListenerData[],
  track: Track,
) => SourceRow[] = (listenerData, track) => {
  const sourceData: Record<string, SourceRow> = {}

  listenerData.forEach(ld => {
    ld.events.forEach(eventData => {
      const sourceName = resolveSourceName(eventData, track)
      let row = sourceData[sourceName]

      if (!row) {
        row = {
          ...emptySourceCounts(eventData, track, sourceName),
          uniqueListeners: [],
        }
        sourceData[sourceName] = row
      }

      if (!row.uniqueListeners.includes(ld.listener_id)) {
        row.uniqueListeners.push(ld.listener_id)
      }

      const eventKey = columnForEvent(eventData)
      if (eventKey) row[eventKey] += 1
    })
  })

  return Object.values(sourceData)
}

/**
 * Breaks a single listener's activity down by where it came from, for the rows
 * revealed when their table row is expanded. Uses the events already carried on
 * the row, so expanding costs no request.
 */
export const getListenerSourceBreakdown = (
  row: TransformedListenerData,
  track: Track,
): ListenerSourceRow[] => {
  const breakdown: Record<string, ListenerSourceRow> = {}

  row.events.forEach(eventData => {
    const sourceName = resolveSourceName(eventData, track)
    let sourceRow = breakdown[sourceName]

    if (!sourceRow) {
      sourceRow = emptySourceCounts(eventData, track, sourceName)
      breakdown[sourceName] = sourceRow
    }

    const eventKey = columnForEvent(eventData)
    if (eventKey) sourceRow[eventKey] += 1
  })

  return Object.values(breakdown)
}
export const transformListenerData: (
  listenerData: ListenerData[],
  isTrackSharingEnabled: boolean,
) => TransformedListenerData[] = (listenerData, isTrackSharingEnabled) => {
  if (!isTrackSharingEnabled) {
    const allEvents = listenerData.flatMap(item => item.events)
    return [
      {
        Name: 'Anonymous',
        id: -1,
        'Page Views': allEvents.filter(
          event => event.event_type === 'access_track',
        ).length,
        'Downloads/Saves': allEvents.filter(
          event => event.event_type === 'download_track',
        ).length,
        Streams: allEvents.filter(event => event.event_type === 'play_track')
          .length,
        'Last Activity': getLastActivity(allEvents),
        events: allEvents,
      },
    ]
  }

  return listenerData.map(item => ({
    Name: item.listener_name,
    id: item.listener_id,
    'Page Views': item.events.filter(
      event => event.event_type === 'access_track',
    ).length,
    'Downloads/Saves': item.events.filter(
      event => event.event_type === 'download_track',
    ).length,
    Streams: item.events.filter(event => event.event_type === 'play_track')
      .length,
    'Last Activity': getLastActivity(item.events),
    events: item.events,
  }))
}
/**
 * The unattributed row carries the API's 'Anonymous' name but is shown as
 * "Other Recipients", so display and sorting have to agree on one of them.
 */
export const getListenerDisplayName = (row: TransformedListenerData): string =>
  row.id === -1 ? 'Other Recipients' : row.Name

export type ListenerSortColumn =
  | 'name'
  | 'pageViews'
  | 'downloads'
  | 'streams'
  | 'lastActivity'

export const getListenerSortValue = (
  row: TransformedListenerData,
  column: ListenerSortColumn,
): string | number => {
  switch (column) {
    case 'name':
      return getListenerDisplayName(row).toLowerCase()
    case 'pageViews':
      return row['Page Views']
    case 'downloads':
      return row['Downloads/Saves']
    case 'streams':
      return row['Streams']
    case 'lastActivity':
      // Epoch keeps never-active listeners comparable instead of yielding NaN.
      return row['Last Activity']?.getTime() ?? 0
  }
}

export type SourceSortColumn =
  | 'source'
  | 'pageViews'
  | 'downloads'
  | 'streams'
  | 'listeners'

export const getSourceSortValue = (
  row: SourceRow,
  column: SourceSortColumn,
): string | number => {
  switch (column) {
    case 'source':
      return row.Source.sourceText.toLowerCase()
    case 'pageViews':
      return row['Page Views']
    case 'downloads':
      return row['Downloads/Saves']
    case 'streams':
      return row['Streams']
    case 'listeners':
      return row.uniqueListeners.length
  }
}

export const getAggregates: (
  transformedListenerData: TransformedListenerData[],
) => AggregatedStats = transformedListenerData => {
  let aggregates = {
    uniqueListeners: 0,
    pageViews: 0,
    downloads: 0,
    streams: 0,
  }

  for (const row of transformedListenerData) {
    aggregates['pageViews'] += row['Page Views']
    aggregates['downloads'] += row['Downloads/Saves']
    aggregates['streams'] += row['Streams']
    aggregates['uniqueListeners'] += 1
  }

  return aggregates
}
